import { ValidationResult, VALID_FLAVOR_NAMES, VALID_ATTRIBUTE_NAMES, VALID_BRAND_SIZES, VALID_LOCATION_TYPES } from './types';
import type { Brand, Flavor, Attribute, Drink, FirestoreLocation, VisitedLocation, DrinkFlavor, DrinkAttribute } from '../../types/firestore';

/**
 * Validates data before import to ensure it meets the expected schema
 * @param jsonData - JSON string of data to validate
 * @returns An object with validation results
 */
export const validateImportData = (jsonData: string): ValidationResult => {
  const issues: Array<{severity: 'error' | 'warning'; message: string}> = [];
  let data: Record<string, any>;
  
  // Try to parse the JSON
  try {
    data = JSON.parse(jsonData);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { 
      valid: false, 
      issues: [{ 
        severity: 'error', 
        message: `Invalid JSON format: ${errorMessage}` 
      }] 
    };
  }
  
  // Validate that at least one collection exists
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
    return { 
      valid: false, 
      issues: [{ 
        severity: 'error', 
        message: 'Import data is empty or invalid. Please provide at least one valid collection.' 
      }] 
    };
  }
  
  // Enhanced debugging: Check for null values in collections directly
  for (const collectionName in data) {
    const collection = data[collectionName];
    
    if (collection === null || collection === undefined) {
      issues.push({
        severity: 'error',
        message: `Collection '${collectionName}' has a null or undefined value`
      });
      continue;
    }
    
    if (Array.isArray(collection)) {
      // Check for null items in arrays
      collection.forEach((item, index) => {
        if (item === null || item === undefined) {
          issues.push({
            severity: 'error',
            message: `Collection '${collectionName}' contains a null or undefined item at index ${index}`
          });
        }
      });
    } else if (typeof collection === 'object') {
      // Check for null values in object entries
      for (const key in collection) {
        if (collection[key] === null || collection[key] === undefined) {
          issues.push({
            severity: 'error',
            message: `Collection '${collectionName}' contains a null or undefined value for key '${key}'`
          });
        }
      }
    } else {
      issues.push({
        severity: 'error',
        message: `Collection '${collectionName}' has an invalid type: ${typeof collection}. Must be an array or object.`
      });
    }
  }
  
  // Check for supported collections
  const supportedCollections = new Set([
    'flavors', 'attributes', 'brands', 'drinks', 'locations', 'sightings', 'permissions', 
    'reviews', 'visitedLocations', 'users'
  ]);
  
  // Check for unsupported collections
  Object.keys(data).forEach(collection => {
    if (!supportedCollections.has(collection)) {
      issues.push({
        severity: 'warning',
        message: `Unsupported collection "${collection}" will be ignored during deep validation, but import will still be attempted.`
      });
    }
  });
  
  // Validate structure of collections
  
  // Validate brands
  if (data.brands) {
    try {
      const brandEntries: Partial<Brand>[] = Array.isArray(data.brands) ? data.brands : Object.values(data.brands);
      const brandIds = new Set();
      const brandNames = new Set();
      
      brandEntries.forEach((brand: Partial<Brand>, index: number) => {
        // Safety check for null objects
        if (!brand) {
          issues.push({
            severity: 'error',
            message: `Null or undefined brand at index ${index}`
          });
          return; // Skip further checks for this item
        }
        
        // ID field is optional - it will be generated if missing
        if (brand.id) {
          if (brandIds.has(brand.id)) {
            issues.push({
              severity: 'error',
              message: `Duplicate brand ID: "${brand.id}"`
            });
          } else {
            brandIds.add(brand.id);
          }
        }
        
        // Name is required
        if (!brand.name) {
          issues.push({
            severity: 'error',
            message: `Brand at index ${index} is missing required 'name' field`
          });
        } else if (brandNames.has(brand.name)) {
          issues.push({
            severity: 'error',
            message: `Duplicate brand name: "${brand.name}". Names must be unique regardless of ID.`
          });
        } else {
          brandNames.add(brand.name);
        }
        
        // Check brand size validity
        if (brand.brandSize && !VALID_BRAND_SIZES.includes(brand.brandSize)) {
          issues.push({
            severity: 'error',
            message: `Brand "${brand.name || brand.id || index}" has invalid 'brandSize' value: "${brand.brandSize}". Valid values are: ${VALID_BRAND_SIZES.join(', ')}`
          });
        }
        
        // Check for unexpected fields
        const expectedFields = ['id', 'name', 'brandSize', 'createdAt', 'updatedAt'];
        Object.keys(brand).forEach(field => {
          if (!expectedFields.includes(field)) {
            issues.push({
              severity: 'warning',
              message: `Brand "${brand.name || brand.id || index}" contains unexpected field: "${field}"`
            });
          }
        });
      });
    } catch (error) {
      // Catch any unexpected errors during brand validation
      const errorMsg = error instanceof Error ? error.message : String(error);
      issues.push({
        severity: 'error',
        message: `Error validating brands: ${errorMsg}`
      });
    }
  }
  
  // Validate flavors
  if (data.flavors) {
    const flavorEntries: Partial<Flavor>[] = Array.isArray(data.flavors) ? data.flavors : Object.values(data.flavors);
    const flavorIds = new Set();
    const flavorNames = new Set();
    
    flavorEntries.forEach((flavor: Partial<Flavor>, index: number) => {
      // ID field is optional - it will be generated if missing
      if (flavor.id && flavorIds.has(flavor.id)) {
        issues.push({
          severity: 'error',
          message: `Duplicate flavor ID: "${flavor.id}"`
        });
      } else if (flavor.id) {
        flavorIds.add(flavor.id);
      }
      
      // Name is required
      if (!flavor.name) {
        issues.push({
          severity: 'error',
          message: `Flavor at index ${index} is missing required 'name' field`
        });
      } else {
        if (flavorNames.has(flavor.name)) {
          issues.push({
            severity: 'error',
            message: `Duplicate flavor name: "${flavor.name}". Names must be unique regardless of ID.`
          });
        } else {
          flavorNames.add(flavor.name);
        }
        
        if (!VALID_FLAVOR_NAMES.includes(flavor.name)) {
          issues.push({
            severity: 'warning',
            message: `Flavor "${flavor.name}" is not in the list of standard flavors. Consider using one of: ${VALID_FLAVOR_NAMES.join(', ')}`
          });
        }
      }
      
      // Check for unexpected fields
      const expectedFields = ['id', 'name', 'groupId', 'orderInGroup', 'createdAt', 'updatedAt'];
      Object.keys(flavor).forEach(field => {
        if (!expectedFields.includes(field)) {
          issues.push({
            severity: 'warning',
            message: `Flavor "${flavor.name || flavor.id}" contains unexpected field: "${field}"`
          });
        }
      });
    });
  }
  
  // Validate attributes
  if (data.attributes) {
    const attributeEntries: Partial<Attribute>[] = Array.isArray(data.attributes) ? data.attributes : Object.values(data.attributes);
    const attributeIds = new Set();
    const attributeNames = new Set();
    
    attributeEntries.forEach((attribute: Partial<Attribute>, index: number) => {
      // ID field is optional - it will be generated if missing
      if (attribute.id && attributeIds.has(attribute.id)) {
        issues.push({
          severity: 'error',
          message: `Duplicate attribute ID: "${attribute.id}"`
        });
      } else if (attribute.id) {
        attributeIds.add(attribute.id);
      }
      
      // Name is required
      if (!attribute.name) {
        issues.push({
          severity: 'error',
          message: `Attribute at index ${index} is missing required 'name' field`
        });
      } else {
        if (attributeNames.has(attribute.name)) {
          issues.push({
            severity: 'error',
            message: `Duplicate attribute name: "${attribute.name}". Names must be unique regardless of ID.`
          });
        } else {
          attributeNames.add(attribute.name);
        }
        
        if (!VALID_ATTRIBUTE_NAMES.includes(attribute.name)) {
          issues.push({
            severity: 'warning',
            message: `Attribute "${attribute.name}" is not in the list of standard attributes. Consider using one of: ${VALID_ATTRIBUTE_NAMES.join(', ')}`
          });
        }
      }
      
      // Check for unexpected fields
      const expectedFields = ['id', 'name', 'groupId', 'orderInGroup', 'createdAt', 'updatedAt'];
      Object.keys(attribute).forEach(field => {
        if (!expectedFields.includes(field)) {
          issues.push({
            severity: 'warning',
            message: `Attribute "${attribute.name || attribute.id}" contains unexpected field: "${field}"`
          });
        }
      });
    });
  }
  
  // Validate drinks
  if (data.drinks) {
    try {
      const drinkEntries: Partial<Drink>[] = Array.isArray(data.drinks) ? data.drinks : Object.values(data.drinks);
      const drinkIds = new Set();
      const drinkIdentifiers = new Set();
      
      // Get brand information for reference checking
      const brandEntries: any[] = data.brands ? 
        (Array.isArray(data.brands) ? data.brands : Object.values(data.brands)) : 
        [];
      
      // Create brand name-to-id lookup map for brand name references
      const brandNameToIdMap = new Map<string, string>();
      brandEntries.forEach((brand: any) => {
        if (brand && brand.name && brand.id) {
          brandNameToIdMap.set(brand.name.toLowerCase(), brand.id);
        }
      });
      
      drinkEntries.forEach((drink: Partial<Drink>, index: number) => {
        if (!drink) {
          issues.push({ severity: 'error', message: `Null or undefined drink at index ${index}` });
          return;
        }
        
        // Validate flavors array early to catch null issues
        if (drink.flavors !== undefined) {
          if (!Array.isArray(drink.flavors)) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has an invalid 'flavors' field - must be an array`
            });
          } else if (drink.flavors === null) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has null 'flavors' array`
            });
          } else {
            // Check for null entries in the array
            for (let i = 0; i < drink.flavors.length; i++) {
              const flavor = drink.flavors[i];
              if (flavor === null || flavor === undefined) {
                issues.push({
                  severity: 'error',
                  message: `Drink "${drink.name || drink.id || 'at index ' + index}" has null or undefined flavor at index ${i}`
                });
              }
            }
          }
        }
        
        // Validate attributes array early to catch null issues
        if (drink.attributes !== undefined) {
          if (!Array.isArray(drink.attributes)) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has an invalid 'attributes' field - must be an array`
            });
          } else if (drink.attributes === null) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has null 'attributes' array`
            });
          } else {
            // Check for null entries in the array
            for (let i = 0; i < drink.attributes.length; i++) {
              const attribute = drink.attributes[i];
              if (attribute === null || attribute === undefined) {
                issues.push({
                  severity: 'error',
                  message: `Drink "${drink.name || drink.id || 'at index ' + index}" has null or undefined attribute at index ${i}`
                });
              }
            }
          }
        }
        
        // Continue with the rest of drink validation
        // ID field is optional - it will be generated if missing
        if (drink.id && drinkIds.has(drink.id)) {
          issues.push({
            severity: 'error',
            message: `Duplicate drink ID: "${drink.id}"`
          });
        } else if (drink.id) {
          drinkIds.add(drink.id);
        }
        
        // Validate drink name (optional - flagship/original drinks may not have a specific name)
        if (drink.name) {
          // Check for uniqueness of brand + name combination
          // Use drink.brandName for validation since that's what's in the import data
          const brandNameForIdentifier = drink.brandName || drink.brandId || 'unknown_brand';
          const drinkIdentifier = `${brandNameForIdentifier}-${drink.name}`;
          if (drinkIdentifiers.has(drinkIdentifier)) {
            issues.push({
              severity: 'error',
              message: `Duplicate drink: Brand "${drink.brandName || drink.brandId || 'Unknown Brand'}" already has a drink named "${drink.name}"`
            });
          } else {
            drinkIdentifiers.add(drinkIdentifier);
          }
        } else {
          // Drink has no name - this is expected for flagship/original drinks
          // Check for duplicate flagship drinks (same brand with no name)
          const brandNameForIdentifier = drink.brandName || drink.brandId || 'unknown_brand';
          const flagshipIdentifier = `${brandNameForIdentifier}-flagship`;
          if (drinkIdentifiers.has(flagshipIdentifier)) {
            issues.push({
              severity: 'error',
              message: `Duplicate flagship drink: Brand "${drink.brandName || drink.brandId || 'Unknown Brand'}" already has a flagship drink (drink without name)`
            });
          } else {
            drinkIdentifiers.add(flagshipIdentifier);
          }
        }
        
        // Validate brandId and brandName - accept either one for import data
        if (!drink.brandId && !drink.brandName) {
          issues.push({ severity: 'error', message: `Drink "${drink.name || index}" is missing both 'brandId' and 'brandName' fields. At least one is required.` });
        }
        
        // If brandName is provided, check if it matches a brand in the import data
        if (drink.brandName && brandNameToIdMap.size > 0) {
          const brandLookupKey = drink.brandName.toLowerCase();
          if (!brandNameToIdMap.has(brandLookupKey)) {
            issues.push({ 
              severity: 'warning', 
              message: `Drink "${drink.name || index}" references brand "${drink.brandName}" which was not found in the brands collection.` 
            });
          }
        }
        
        // If only brandId is provided, warn about missing brandName
        if (drink.brandId && !drink.brandName) {
          issues.push({ severity: 'warning', message: `Drink "${drink.name || drink.id || index}" has 'brandId' but is missing 'brandName'. Ensure 'brandId' (Value: ${drink.brandId}) is valid.` });
        }
        
        // Validate brandSize
        if (drink.brandSize && !VALID_BRAND_SIZES.includes(drink.brandSize)) {
          issues.push({
            severity: 'error',
            message: `Drink "${drink.name || drink.id || (drink.brandName || drink.brandId)}" has invalid 'brandSize': "${drink.brandSize}". Valid values: ${VALID_BRAND_SIZES.join(', ')}`
          });
        }
        
        // Validate flavors (array of objects - accept either id+name OR just name for import data)
        if (drink.flavors) {
          if (!Array.isArray(drink.flavors)) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has an invalid 'flavors' field - must be an array`
            });
          } else {
            drink.flavors.forEach((flavor: Partial<DrinkFlavor>, fidx: number) => {
              if (!flavor || typeof flavor !== 'object') {
                issues.push({ severity: 'error', message: `Drink "${drink.name || drink.id || 'at index ' + index}" has invalid flavor item at index ${fidx}: not an object`});
                return;
              }
              // For import data, we only require 'name' - 'id' will be resolved during import
              if (!flavor.name) {
                issues.push({ severity: 'error', message: `Flavor for drink "${drink.name || drink.id || 'at index ' + index}" at index ${fidx} is missing required 'name' field` });
              }
              // 'id' is optional for import data - it will be resolved from 'name'
              if (!flavor.id && !flavor.name) {
                issues.push({ severity: 'error', message: `Flavor for drink "${drink.name || drink.id || 'at index ' + index}" at index ${fidx} is missing both 'id' and 'name' fields. At least 'name' is required for import data.` });
              }
              // Check for unexpected fields in drink.flavors
              const expectedFlavorFields = ['id', 'name', 'groupId'];
              Object.keys(flavor).forEach(field => {
                if (!expectedFlavorFields.includes(field)) {
                  issues.push({ severity: 'warning', message: `Flavor for drink "${drink.name || drink.id || 'at index ' + index}" at index ${fidx} contains unexpected field: "${field}"` });
                }
              });
            });
          }
        }
        
        // Validate attributes array (accept either id+name OR just name for import data)
        if (drink.attributes) {
          if (!Array.isArray(drink.attributes)) {
            issues.push({
              severity: 'error',
              message: `Drink "${drink.name || drink.id || 'at index ' + index}" has an invalid 'attributes' field - must be an array`
            });
          } else {
            drink.attributes.forEach((attribute: Partial<DrinkAttribute>, aidx: number) => {
              if (!attribute || typeof attribute !== 'object') {
                issues.push({ severity: 'error', message: `Drink "${drink.name || drink.id || 'at index ' + index}" has invalid attribute item at index ${aidx}: not an object`});
                return;
              }
              // For import data, we only require 'name' - 'id' will be resolved during import
              if (!attribute.name) {
                issues.push({ severity: 'error', message: `Attribute for drink "${drink.name || drink.id || 'at index ' + index}" at index ${aidx} is missing required 'name' field` });
              }
              // 'id' is optional for import data - it will be resolved from 'name'
              if (!attribute.id && !attribute.name) {
                issues.push({ severity: 'error', message: `Attribute for drink "${drink.name || drink.id || 'at index ' + index}" at index ${aidx} is missing both 'id' and 'name' fields. At least 'name' is required for import data.` });
              }
              // Check for unexpected fields in drink.attributes
              const expectedAttributeFields = ['id', 'name', 'groupId'];
              Object.keys(attribute).forEach(field => {
                if (!expectedAttributeFields.includes(field)) {
                  issues.push({ severity: 'warning', message: `Attribute for drink "${drink.name || drink.id || 'at index ' + index}" at index ${aidx} contains unexpected field: "${field}"` });
                }
              });
            });
          }
        }
      });
    } catch (error) {
      // Catch any unexpected errors during drink validation
      const errorMsg = error instanceof Error ? error.message : String(error);
      issues.push({
        severity: 'error',
        message: `Error validating drinks: ${errorMsg}`
      });
    }
  }
  
  // Validate locations (basic structure checks)
  if (data.locations) {
    const locationEntries: Partial<FirestoreLocation>[] = Array.isArray(data.locations) ? data.locations : Object.values(data.locations);
    const locationIds = new Set();

    locationEntries.forEach((location: Partial<FirestoreLocation>, index: number) => {
      // ID is optional - will be generated if missing
      if (!location.id) {
        issues.push({
          severity: 'warning', // Changed from error to warning
          message: `Location at index ${index} is missing 'id' field, one will be generated during import.`
        });
      } else if (locationIds.has(location.id)) {
        issues.push({
          severity: 'error',
          message: `Duplicate location ID: "${location.id}"`
        });
      } else {
        locationIds.add(location.id);
      }

      // Name is required
      if (!location.name) {
        issues.push({
          severity: 'error',
          message: `Location ID "${location.id || 'at index ' + index}" is missing required 'name' field`
        });
      }

      // Coordinates are required
      if (!location.coordinates || typeof location.coordinates !== 'object') {
        issues.push({
          severity: 'error',
          message: `Location ID "${location.id || 'at index ' + index}" is missing required 'coordinates' field or it's not an object`
        });
      } else {
        const coords = location.coordinates as { latitude?: unknown; longitude?: unknown }; 
        const lat = coords.latitude;
        const lon = coords.longitude;

        if (typeof lat !== 'number' || typeof lon !== 'number') {
           issues.push({
            severity: 'error',
            message: `Location ID "${location.id || 'at index ' + index}" has invalid latitude (${lat}) or longitude (${lon}). Both must be numbers.`
          });
        } else if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          issues.push({
            severity: 'warning', // Warning, not error, as Firestore allows this
            message: `Location ID "${location.id || 'at index ' + index}" has out-of-range latitude (${lat}) or longitude (${lon}). Should be within [-90, 90] and [-180, 180].`
          });
        }
      }
      
      // Validate locationType
      if (location.locationType && !VALID_LOCATION_TYPES.includes(location.locationType)) {
        // Special case for "store" -> "shop" mapping
        if ((location.locationType as string) === 'store') {
          issues.push({
            severity: 'warning',
            message: `Location ID "${location.id || 'at index ' + index}" has locationType "store" which will be mapped to "shop" during import.`
          });
        } else {
          issues.push({
            severity: 'error',
            message: `Location ID "${location.id || 'at index ' + index}" has invalid 'locationType' value: "${location.locationType}". Valid values are: ${VALID_LOCATION_TYPES.join(', ')}`
          });
        }
      }

      // Check for expected fields (add more as needed)
      const expectedFields = [
          'id', 'name', 'address', 'url', 'locationType', 'coordinates', 
          'averageRating', 'ratingCount', 'drinkCount', 'lastSightingDate', // Allow common aggregated fields
          'createdAt', 'updatedAt', 'googlePlaceId' // Common timestamp and reference fields
      ];
      Object.keys(location).forEach(field => {
        if (!expectedFields.includes(field)) {
          issues.push({
            severity: 'warning',
            message: `Location "${location.name || location.id}" contains unexpected field: "${field}"`
          });
        }
      });
    });
  }
  
  // Validate sightings
  if (data.sightings) {
    // ... existing sighting validation ...
  }
  
  // Validate visited locations
  if (data.visitedLocations) {
      const visitedEntries: Partial<VisitedLocation>[] = Array.isArray(data.visitedLocations) ? data.visitedLocations : Object.values(data.visitedLocations);
      const visitedLocationIds = new Set();

      // Define an interface for the expected shape of a serialized timestamp in the JSON input
      interface SerializedTimestampInput {
        _type?: string;
        seconds?: unknown;
        nanoseconds?: unknown;
      }

      visitedEntries.forEach((visited: Partial<VisitedLocation>, index: number) => {
          // ID is required and should generally match a location ID
          if (!visited.id) {
              issues.push({
                  severity: 'error',
                  message: `Visited Location at index ${index} is missing required 'id' field`
              });
          } else if (visitedLocationIds.has(visited.id)) {
               // Allow multiple visits per location, but maybe warn if same ID appears multiple times *in this import*?
               // For now, just track IDs seen in this collection import
               visitedLocationIds.add(visited.id);
          } else {
              visitedLocationIds.add(visited.id);
          }
          
          // Rating should be a number between 0 and 5 if present
          if (visited.rating !== undefined && (typeof visited.rating !== 'number' || visited.rating < 0 || visited.rating > 5)) {
               issues.push({
                  severity: 'error',
                  message: `Visited Location ID "${visited.id}" has invalid 'rating': ${visited.rating}. Must be a number between 0 and 5.`
              });
          }
          
          // visitDate is required and should be a timestamp object
          const visitDateInput = visited.visitDate as SerializedTimestampInput | undefined;
          if (!visitDateInput || typeof visitDateInput !== 'object' || visitDateInput._type !== 'timestamp') {
              issues.push({
                  severity: 'error',
                  message: `Visited Location ID "${visited.id}" is missing or has invalid 'visitDate'. Must be an object like { _type: 'timestamp', seconds: ..., nanoseconds: ... }.`
              });
          } else if (typeof visitDateInput.seconds !== 'number' || typeof visitDateInput.nanoseconds !== 'number') {
               issues.push({
                  severity: 'error',
                  message: `Visited Location ID "${visited.id}" has invalid timestamp structure in 'visitDate'. Missing or non-numeric seconds/nanoseconds.`
              });
          }
          
          // Check for expected fields
          const expectedFields = ['id', 'rating', 'publicNote', 'privateNote', 'visitDate', 'locationName']; // Add locationName for context
          Object.keys(visited).forEach(field => {
              if (!expectedFields.includes(field)) {
                  issues.push({
                      severity: 'warning',
                      message: `Visited Location ID "${visited.id}" contains unexpected field: "${field}"`
                  });
              }
          });
      });
  }
  
  // Validate permissions
  if (data.permissions) {
    // ... existing permissions validation ...
  }
  
  // Validate reviews
  if (data.reviews) {
    // ... existing reviews validation ...
  }
  
  // Count critical errors (non-warning issues)
  const criticalErrors = issues.filter(issue => issue.severity === 'error').length;
  
  return {
    valid: criticalErrors === 0,
    issues
  };
}; 